import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Chip } from "@/components/Chip";
import { Modal } from "@/components/Modal";
import { SearchInput, Select } from "@/components/inputs";
import { ErrorNote } from "@/components/ErrorNote";
import { products, type SpecField, type SpecGroup } from "@/api/client";
import { qk, useProductSpec } from "@/modules/products/data";

/** The spec-field editor — what makes "Add product" mean anything.
 *
 *  I flagged the cost when Dattatray asked for Add product: a product is a
 *  catalogue `kind`, and the SPEC TEMPLATE keyed by that id is what the Asset
 *  Catalog renders a model form from. A kind created without one shows an
 *  EMPTY form. His answer: *"Add product : build the spec-field editor too."*
 *
 *  Before 0064 there was nowhere for an edit to go — the templates were
 *  Python literals. Now they are a JSONB column, `/modules/asset-catalog/
 *  templates` reads it, and this writes it. ONE source: the literals are the
 *  seed, and `tests/db/test_kinds_are_a_table.py` keeps them honest.
 *
 *  TWO THINGS ARE PERMANENT AND THE EDITOR SAYS SO RATHER THAN HIDING IT:
 *
 *    the product id    five foreign keys point at it, and every tool sends it
 *    a field's KEY     a model's specs are stored UNDER that key, so renaming
 *                      it orphans every value already saved. The input is
 *                      disabled once the field exists; add a new one instead.
 *
 *  EDITS ARE LOCAL UNTIL SAVED. The whole spec goes in one PUT, because the
 *  server validates it as a whole — a duplicate key is only visible across
 *  fields — and because a half-applied spec is a form that has lost a
 *  section.
 */
export function SpecEditor({ kind }: { kind: string }) {
  const q = useProductSpec(kind);
  const qc = useQueryClient();
  const [draft, setDraft] = useState<SpecGroup[] | null>(null);
  const [saved, setSaved] = useState(0);

  // Load the server's copy into the draft once it arrives, and again whenever
  // the product changes. Keyed on `kind` so switching products cannot leave
  // the previous one's fields on screen — the D-84 failure, one level down.
  useEffect(() => { setDraft(null); }, [kind]);
  const spec = draft ?? q.data?.spec ?? [];

  const save = useMutation({
    mutationFn: () => products.setSpec(kind, spec),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.spec(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
      // The Asset Catalog renders its model form from this, so its cached
      // templates are stale the moment this lands.
      void qc.invalidateQueries({ queryKey: ["asset-catalog"] });
      setDraft(null);
      setSaved((n) => n + 1);
    },
  });

  const edit = (next: SpecGroup[]) => setDraft(next);
  const dirty = draft !== null;

  if (q.isLoading) return <div className="panel panel-pad cap">Loading…</div>;

  return (
    <div>
      {/* Label on its own line, chips beneath — the pattern every other
          section-label follows. It was a label INSIDE the flex row with
          `style={{ margin: 0 }}`, which `test_section_label_spacing_is_declared_
          not_retyped` refuses: the class owns the rhythm, and an inline margin
          is that decision retyped where nobody will find it again. */}
      <div className="section-label tight">Spec fields</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <Chip tone="mute">
          {spec.reduce((n, g) => n + g.fields.length, 0)} in {spec.length} group
          {spec.length === 1 ? "" : "s"}
        </Chip>
        {dirty && <Chip tone="warn">unsaved</Chip>}
        {!dirty && saved > 0 && <Chip tone="ok">saved</Chip>}
      </div>


      {/* ONE line. The previous version had a bordered panel with a
          heading and a three-sentence explanation; Dattatray: *"Un-necessary
          comments make this UI cluttered."* An interface that has to explain
          itself at length is usually mis-laid-out, and the fix is the layout. */}
      <div className="cap" style={{ marginBottom: 10 }}>
        What you record about every model of this product.
      </div>

      {spec.length === 0 && (
        <div className="panel panel-pad cap">No fields yet. Add a group to start.</div>
      )}

      {spec.map((g, gi) => (
        <Group key={gi} group={g}
          existing={new Set((q.data?.spec ?? []).flatMap((x) => x.fields.map((f) => f.k)))}
          onChange={(next) => edit(spec.map((x, i) => (i === gi ? next : x)))}
          onRemove={() => edit(spec.filter((_, i) => i !== gi))} />
      ))}


      {/* THE ACTIONS ARE AT THE FOOT. Dattatray: *"we should have save Spec
          and Discard button at the bottom instead of top."* They were at the
          top, which is where you are before you edit and never where you are
          after — on `cluster_c` that is 16 fields of scrolling back up to
          commit what you just typed.

          The REFUSAL sits with them, in its own colour. It used to render
          below every group, a screen and a half from the button, and a save
          refused down there is indistinguishable from a button that does
          nothing — reported twice as exactly that. */}
      <ErrorNote panel error={save.error} />

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
        <button className="btn ghost sm"
          onClick={() => edit([...spec, { group: "New group", fields: [] }])}>
          Add group
        </button>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {dirty && (
            <button className="btn ghost sm" onClick={() => setDraft(null)}>
              Discard
            </button>
          )}
          <button className="btn teal sm" disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save spec"}
          </button>
        </span>
      </div>

    </div>
  );
}

/** The column widths, declared ONCE and used by the header and every row.
 *
 *  Dattatray: *"Key, Label, Type, Options, Required — these column names need
 *  not be repeated for each row!"* They were, because each field rendered its
 *  own labelled controls. A label per control is right for a FORM and wrong
 *  for a LIST of the same thing twenty times over: it triples the height and
 *  buries the values in their own captions.
 *
 *  So the labels are a header row and the fields are rows under it. The rows
 *  line up because the template lives here and nothing redeclares it.
 */
const COLS = "minmax(180px,1.3fr) 150px minmax(160px,1fr) 80px auto";

/** Every control in a row must obey the grid, and by default none of them do.
 *
 *  `.search` carries `min-width: 210px` (components.css) — a sensible default
 *  for a toolbar, and wrong inside a table. Each control refused to go below
 *  210px, so it overflowed its column and shoved the next one along: the
 *  "Answer" box stretched past its column wherever the neighbouring cell was
 *  small, and "Stored as" ended up jammed against Remove. The columns were
 *  correct; the controls were ignoring them.
 *
 *  `minWidth: 0` is the standard escape — a grid item's automatic minimum is
 *  its content, so without it a cell can never be narrower than what it holds.
 */
const CELL = { minWidth: 0, width: "100%" } as const;

/** Text cells clip; they never push a neighbour.
 *
 *  A grid item's automatic minimum is its CONTENT, so a long unbroken value
 *  widens its own track and every column after it shifts — one long row is
 *  enough to misalign the whole table. `minWidth: 0` lets the track hold its
 *  declared width and the ellipsis shows that something was cut rather than
 *  letting it spill.
 *
 *  Inputs already carry `CELL`, which is the same `minWidth: 0` — an <input>
 *  scrolls its own text, so it needs no ellipsis.
 */
const TEXT_CELL = {
  // `display: block` is not cosmetic — `overflow` and `text-overflow` DO
  // NOTHING on an inline box. Every text cell here was a <span>, so the
  // clipping declared below was inert and a long choice list ran straight
  // across Required and Remove. Measured on `cluster_c`: "6 — dust tight,
  // 5 — dust protected, …" overlapped two columns.
  display: "block",
  minWidth: 0, overflow: "hidden",
  textOverflow: "ellipsis", whiteSpace: "nowrap",
} as const;

function Group({ group, existing, onChange, onRemove }: {
  group: SpecGroup;
  /** Keys already SAVED — those cannot be renamed. */
  existing: Set<string>;
  onChange: (g: SpecGroup) => void;
  onRemove: () => void;
}) {
  const setField = (i: number, f: SpecField) =>
    onChange({ ...group, fields: group.fields.map((x, j) => (j === i ? f : x)) });

  return (
    /* THE FUSION Dattatray asked for, and he named both halves exactly:
     *
     *   "I like the subsection management of Asset Catalog -> Edit/add new
     *    models ... But the actual table format looks good in the
     *    Products -> Spec field. Fuse the best thing, make a consistent UI."
     *
     * The Asset Catalog's model form puts the group name in a `section-label`
     * ABOVE the panel — so "Cluster / Connectivity / Bluetooth / FOTA support
     * / Compliance" read as sections of one page. This panel had the group
     * name as an input INSIDE its own bordered box, so each group looked like
     * a separate widget rather than a section, and the two screens felt like
     * two products.
     *
     * So: heading outside, table inside. The heading is still editable here —
     * the Asset Catalog only READS these groups while this screen defines
     * them — so it is an input wearing the heading's typography, with its
     * border appearing on focus. It looks like the label it is until you go
     * to change it.
     */
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <input value={group.group} placeholder="GROUP NAME"
          aria-label="Group name"
          className="section-label"
          /* NO `font`/`color`/`letterSpacing` here. They were set to
             "inherit", which OVERRODE the whole point of `.section-label` —
             10px, weight 800, 1.8px letter-spacing, uppercase, --t-dim — so
             the heading rendered in the surrounding body font and did not
             match the Asset Catalog's section headings at all. The class owns
             the typography; this only makes the input invisible until
             focused. */
          style={{
            margin: "18px 0 8px", background: "transparent", border: "1px solid transparent",
            borderRadius: 6, padding: "2px 6px", minWidth: 0, width: 280,
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = "var(--line2)"; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = "transparent"; }}
          onChange={(e) => onChange({ ...group, group: e.target.value })} />
        <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button className="btn ghost sm" onClick={() => onChange({
            ...group,
            fields: [...group.fields, { k: "", label: "", type: "text" }],
          })}>Add field</button>
          <button className="btn ghost sm" onClick={onRemove}>Remove group</button>
        </span>
      </div>

      {group.fields.length === 0 ? (
        <div className="panel" style={{ padding: "10px 14px" }}>
          <span className="cap">No fields in this group.</span>
        </div>
      ) : (
        <div className="panel" style={{ padding: "8px 14px 12px", display: "grid",
          gridTemplateColumns: COLS, gap: "6px 10px",
          alignItems: "center", minWidth: 0 }}>
          {/* The labels, once. */}
          <div className="cap" style={TEXT_CELL}>Name on the form</div>
          <div className="cap" style={TEXT_CELL}>Answer</div>
          <div className="cap" style={TEXT_CELL}>Unit</div>
          <div className="cap" style={TEXT_CELL}>Mandatory</div>
          <div />

          {group.fields.map((f, i) => (
            <Field key={i} field={f} locked={existing.has(f.k)}
              onChange={(next) => setField(i, next)}
              onRemove={() => onChange({
                ...group, fields: group.fields.filter((_, j) => j !== i),
              })} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One row. A FRAGMENT, not a wrapper: the cells have to be direct children of
 *  the group's grid, or each row becomes its own box and the columns stop
 *  lining up — which is the bug being fixed, reintroduced one level down.
 */
function Field({ field, locked, onChange, onRemove }: {
  field: SpecField;
  /** Already saved, so its KEY is permanent: a model's specs are stored under
   *  it and renaming orphans every value already recorded. */
  locked: boolean;
  onChange: (f: SpecField) => void;
  onRemove: () => void;
}) {
  const [editingList, setEditingList] = useState(false);

  return (
    <>
      {/* THE KEY IS NO LONGER A COLUMN. Dattatray: *"Why do I need to see
          Stored as column."* He does not — it is storage, and once a field is
          saved it is frozen anyway, so the column was a read-only cell taking
          a fifth of the width on every row forever.
          
          For an UNSAVED field it still has to come from somewhere, so it is
          derived from the label. That is safe precisely because `locked` means
          "already saved": an unlocked field has no stored values pointing at
          its key, so the key may follow the label freely. Once saved, the
          label can still be renamed and the key stops moving — which is the
          rule that was always there, now with nothing to read. */}
      <SearchInput value={field.label} placeholder="Nominal voltage" style={CELL}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const label = e.target.value;
          onChange(locked
            ? { ...field, label }
            : { ...field, label, k: slug(label) });
        }} />

      {/* DERIVED IS SHOWN, LOCKED, AND NEVER OFFERED.
       *
       *  A derived field is computed on read by `derive()` and never stored —
       *  IP rating from the two IP digits, a battery's kWh and chemistry.
       *  Seven products carry one, and until today the server rejected the
       *  type outright, so none of them could save their own spec.
       *
       *  It is not in the dropdown because DECLARING one does not make
       *  anything compute it; the computation lives in code. And an existing
       *  one cannot be retyped, because changing `derived` to `text` turns a
       *  computed fact into a stored one that nothing fills in. */}
      {field.type === "derived" ? (
        <span className="cap" style={TEXT_CELL} title="Computed from other fields — not typed in">
          Computed
        </span>
      ) : (
        <Select value={field.type} style={CELL}
          onChange={(e) => onChange({ ...field, type: e.target.value as SpecField["type"] })}>
          <option value="text">Text</option>
          <option value="num">Number</option>
          <option value="select">One of a list</option>
          <option value="bool">Yes / no</option>
          <option value="date">Date</option>
        </Select>
      )}

      {/* One slot that changes CONTENTS. A column that appears and disappears
          shifts every column after it, per row. A long CHOICE list opens a
          popup rather than growing — Dattatray: *"If you want to have pop up
          that can edit long string I am OK but dont change the alignment."*
          The full-width line tried before was worse than the truncation it
          fixed: it broke the very alignment it sat inside. */}
      {field.type === "num" ? (
        <SearchInput value={field.unit ?? ""} placeholder="V" style={CELL}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange({ ...field, unit: e.target.value || undefined })} />
      ) : field.type === "select" ? (
        <button className="btn ghost sm"
          style={{ ...CELL, ...TEXT_CELL, textAlign: "left" }}
          onClick={() => setEditingList(true)}
          title={(field.opts ?? []).join(", ")}>
          {(field.opts ?? []).length
            ? (field.opts ?? []).join(", ")
            : "Add choices…"}
        </button>
      ) : <span className="cap" style={TEXT_CELL}>—</span>}

      <label style={{ display: "flex", justifyContent: "center" }}>
        <input type="checkbox" checked={!!field.req}
          onChange={(e) => onChange({ ...field, req: e.target.checked || undefined })} />
      </label>

      <button className="btn ghost sm" onClick={onRemove}>Remove</button>

      {/* A POPUP, not another row. It is outside the grid flow, so a list of
          any length cannot touch the alignment. */}
      {editingList && (
        <ChoicesModal field={field} onChange={onChange}
          onClose={() => setEditingList(false)} />
      )}
    </>
  );
}

/** A storage key from a label: lower case, underscores, nothing exotic.
 *  Only ever applied to a field that has NOT been saved (see `locked`), so
 *  nothing is stored under the old spelling.
 */
function slug(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 32);
}


/** The choices popup, one per line.
 *
 *  IT HOLDS THE RAW TEXT, and that is the whole reason it is a component.
 *  The first version bound the textarea straight to `field.opts.join("
")`
 *  and parsed on every keystroke with `.filter(Boolean)`. So pressing Enter
 *  made an empty line, the filter removed it, the value was recomputed
 *  without it, and the caret snapped back — *"the Enter key does nothing so I
 *  am not able to add a new line"*. The key worked; the round trip through a
 *  controlled value deleted the result.
 *
 *  A blank line is a legitimate INTERMEDIATE state and an illegitimate saved
 *  one. So the text is local while the popup is open, and only the parsed,
 *  blank-free list is handed upward.
 */
export function ChoicesModal({ field, onChange, onClose }: {
  field: SpecField;
  onChange: (f: SpecField) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState(() => (field.opts ?? []).join("\n"));

  const parse = (raw: string) =>
    raw.split("\n").map((x) => x.trim()).filter(Boolean);

  const commit = (raw: string) => {
    setText(raw);
    onChange({ ...field, opts: parse(raw) });
  };

  const count = parse(text).length;

  return (
    <Modal onClose={onClose}
      title={`Choices for ${field.label || "this field"}`}
      footer={<button className="btn teal sm" onClick={onClose}>Done</button>}>
      <textarea className="search mono" rows={10} autoFocus
        style={{ width: "100%", minWidth: 0, resize: "vertical", lineHeight: 1.7 }}
        value={text}
        placeholder={"5G\n4G Cat-4\n4G Cat-1\n4G Cat-1bis"}
        onChange={(e) => commit(e.target.value)} />
      <div className="cap" style={{ marginTop: 6 }}>
        One per line. {count} so far.
      </div>
    </Modal>
  );
}
